<template>
  <footer class="footer">
    <div :class="[route.name === 'markets-marketName' ? 'container-lg' : 'container']">
      <div v-if="$device.isDesktop" class="row">
        <div class="col-2">
          <paribu-self />
          <social-medias />
        </div>

        <div class="col-2">
          <div class="mb-32">
            <link-items caption="paribu.discover" :links="discover" />
          </div>
          <div class="mb-32">
            <link-items caption="paribu.guide" :links="guide" />
          </div>
        </div>

        <div class="col-2">
          <div class="mb-32">
            <link-items :caption="$t('paribu.footer.crypto_prices')" :links="cryptoPrices" />
          </div>
          <div class="mb-32">
            <link-items
              :caption="$t('paribu.footer.price_predictions')"
              :links="pricePredictions"
            />
          </div>
          <link-items
            style="margin-top: 48px"
            :caption="$t('paribu.footer.calculator')"
            :links="calculator"
          />
        </div>

        <div class="col-2">
          <div class="mb-32">
            <link-items caption="paribu.priceHistory.title" :links="priceHistory" />
          </div>
        </div>

        <div class="col-2">
          <link-items
            caption="paribu.legal_information.section_label"
            :links="legal"
            rel="nofollow"
          />
        </div>
        <div class="col-2">
          <div class="footer__qrcode--wrapper">
            <div class="footer__qrcode">
              <ClientOnly>
                <p-qr-code :size="168" image="/icon/paribu.svg" />
              </ClientOnly>
            </div>
            <div class="footer__qrcode--text">
              <p class="f-utility-14 t-fg-tertiary" v-html="$t('downloadApp.footerScanQr')" />
            </div>
            <div class="footer__store">
              <div class="d-flex mb-12">
                <a
                  href="https://apps.apple.com/tr/app/paribu-bitcoin-al%C4%B1m-sat%C4%B1m/id1448200352?l=tr"
                  title="Paribu App Store"
                  target="_blank"
                  rel="nofollow"
                >
                  <ClientOnly>
                    <img
                      src="/images/app-store.svg"
                      width="120"
                      height="32"
                      alt="App Store"
                      loading="lazy"
                    />
                  </ClientOnly>
                </a>
              </div>
              <div class="d-flex">
                <a
                  href="https://play.google.com/store/apps/details?id=com.paribu.app"
                  title="Paribu Play Store"
                  target="_blank"
                  rel="nofollow"
                >
                  <ClientOnly>
                    <img
                      src="/images/google-play.svg"
                      width="120"
                      height="32"
                      alt="Google Play"
                      loading="lazy"
                    />
                  </ClientOnly>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="row">
        <div class="col-12">
          <div class="mb-32">
            <link-items caption="paribu.discover" :links="discover" />
          </div>
          <div class="mb-32">
            <link-items caption="paribu.guide" :links="guide" />
          </div>
          <div class="mb-32">
            <link-items :caption="$t('paribu.footer.crypto_prices')" :links="cryptoPrices" />
          </div>
          <div class="mb-32">
            <link-items
              :caption="$t('paribu.footer.price_predictions')"
              :links="pricePredictions"
            />
          </div>
          <div class="mb-32">
            <link-items :caption="$t('paribu.footer.calculator')" :links="calculator" />
          </div>
          <div class="mb-32">
            <link-items caption="paribu.priceHistory.title" :links="priceHistory" />
          </div>
          <div class="mb-32">
            <link-items
              caption="paribu.legal_information.section_label"
              :links="legal"
              rel="nofollow"
            />
          </div>

          <p-divider class="my-40" />

          <social-medias class="mb-40" />

          <paribu-self />
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import ParibuSelf from './ParibuSelf/index.vue'
import SocialMedias from './SocialMedias/index.vue'
import LinkItems from './LinkItems/index.vue'
import {
  LEGAL_SLUGS,
  converterUrl,
  legalUrl,
  predictionUrl,
  priceHistoryUrl,
  priceUrl,
} from './links'

const { t: $t, locale } = useI18n()
const route = useRoute()

const guide = computed(() => [
  {
    name: $t('paribu.how_to_buy_crypto'),
    link: 'https://www.paribu.com/blog/paribu-rehberi/yeni-baslayanlar-icin-kripto-rehberi/',
  },
  {
    name: $t('paribu.what_is_crypto'),
    link: 'https://www.paribu.com/blog/sozluk/kripto-para-nedir/',
  },
  {
    name: $t('paribu.deposit'),
    link: 'https://www.paribu.com/blog/paribu-rehberi/paribuya-tl-yatirma-islemi-nasil-yapilir/',
  },
  {
    name: $t('paribu.withdraw'),
    link: 'https://www.paribu.com/blog/paribu-rehberi/paribuda-tl-cekme-islemi-nasil-yapilir/',
  },
  {
    name: $t('paribu.what_is_token'),
    link: 'https://www.paribu.com/blog/sozluk/token-nedir/',
  },
  {
    name: $t('paribu.what_is_alt_token'),
    link: 'https://www.paribu.com/blog/sozluk/altcoin-nedir/',
  },
])

const discover = computed(() => [
  {
    name: $t('paribu.trainings'),
    link: 'https://www.paribu.com/hub/egitimler',
  },
  {
    name: $t('paribu.events'),
    link: 'https://www.paribu.com/hub/etkinlikler',
  },
  {
    name: $t('paribu.open_positions'),
    // Köksüz adres 307 ile /en'e gidiyor; Türkçe sitede Türkçe sayfa.
    link: 'https://kariyer.paribu.com/tr',
  },
  {
    name: $t('paribu.system_check'),
    link: 'https://status.paribu.com/',
  },
  {
    name: $t('paribu.api_documentation'),
    link: 'https://docs.paribu.com/api',
  },
  {
    name: $t('paribu.market_maker'),
    link: 'https://www.paribu.com/blog/haberler/paribuda-piyasa-yapicilik-programi-basladi/',
  },
  {
    name: $t('paribu.bitcoin.pizza_day'),
    link: 'https://www.paribu.com/bitcoin-pizza-gunu',
  },
])

const legal = computed(() => {
  return LEGAL_SLUGS.map(([key, slug]) => ({
    name: $t(`paribu.legal_information.links.${key}`),
    link: legalUrl(slug),
  }))
})

const priceHistory = computed(() => [
  {
    name: $t('paribu.priceHistory.bitcoin'),
    link: priceHistoryUrl('bitcoin', locale.value),
  },
  {
    name: $t('paribu.priceHistory.ethereum'),
    link: priceHistoryUrl('ethereum', locale.value),
  },
  {
    name: $t('paribu.priceHistory.xrp'),
    link: priceHistoryUrl('ripple', locale.value),
  },
  {
    name: $t('paribu.priceHistory.solana'),
    link: priceHistoryUrl('solana', locale.value),
  },
  {
    name: $t('paribu.priceHistory.doge'),
    link: priceHistoryUrl('dogecoin', locale.value),
  },
])

// www.paribu.com footer'ıyla aynı sabit ilk 5 (micro-web `apps/footer`); hacme göre değişmez.
const topAssets = [
  { name: 'Bitcoin', symbol: 'btc', slug: 'bitcoin' },
  { name: 'Ethereum', symbol: 'eth', slug: 'ethereum' },
  { name: 'XRP', symbol: 'xrp', slug: 'ripple' },
  { name: 'Solana', symbol: 'sol', slug: 'solana' },
  { name: 'Dogecoin', symbol: 'doge', slug: 'dogecoin' },
]

const cryptoPrices = computed(() => {
  return topAssets.map((item) => ({
    name: $t('paribu.footer.price_template', { name: item.name }),
    link: priceUrl(item.slug, locale.value),
  }))
})

const pricePredictions = computed(() => {
  return topAssets.map((item) => ({
    name: $t('paribu.footer.price_prediction_template', { name: item.name }),
    link: predictionUrl(item.slug, locale.value),
  }))
})

// Liranın adı da dile göre: ana sitede TR'de "TL", EN'de "TRY".
const calculator = computed(() => {
  return topAssets.map((item) => ({
    name: `${item.symbol.toUpperCase()} → ${locale.value === 'en' ? 'TRY' : 'TL'}`,
    link: converterUrl(item.slug, locale.value),
  }))
})
</script>
